import { useState } from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

interface TestResult {
  success: boolean;
  status?: number;
  message: string;
  data?: any;
  error?: string;
}

export default function WhatsAppTest() {
  // Webhook Verification Test State
  const [verifyToken, setVerifyToken] = useState('test_token');
  const [verifyChallenge, setVerifyChallenge] = useState('test_challenge_12345');
  const [verifyResult, setVerifyResult] = useState<TestResult | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Webhook Events Test State
  const [eventPayload, setEventPayload] = useState(JSON.stringify({
    object: 'whatsapp_business_account',
    entry: [{
      id: 'test_entry_id',
      changes: [{
        field: 'messages',
        value: {
          messaging_product: 'whatsapp',
          metadata: {
            display_phone_number: '+1234567890',
            phone_number_id: 'test_phone_id',
          },
          messages: [{
            from: '+1234567890',
            id: 'test_message_id',
            timestamp: Date.now().toString(),
            type: 'text',
            text: { body: 'test message' },
          }],
        },
      }],
    }],
  }, null, 2));
  const [eventResult, setEventResult] = useState<TestResult | null>(null);
  const [eventLoading, setEventLoading] = useState(false);

  /**
   * Test webhook verification endpoint
   */
  const testWebhookVerification = async () => {
    setVerifyLoading(true);
    setVerifyResult(null);

    try {
      const url = `/api/whatsapp-webhooks/verify?hub.mode=subscribe&hub.verify_token=${encodeURIComponent(verifyToken)}&hub.challenge=${encodeURIComponent(verifyChallenge)}`;

      const response = await fetch(url, {
        method: 'GET',
      });

      const contentType = response.headers.get('content-type');
      let data;

      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (response.ok) {
        setVerifyResult({
          success: true,
          status: response.status,
          message: 'Webhook verification successful',
          data,
        });
      } else {
        setVerifyResult({
          success: false,
          status: response.status,
          message: 'Webhook verification failed',
          data,
        });
      }
    } catch (error) {
      setVerifyResult({
        success: false,
        message: 'Request failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  /**
   * Test webhook events endpoint
   */
  const testWebhookEvents = async () => {
    setEventLoading(true);
    setEventResult(null);

    try {
      // Validate JSON
      let payload;
      try {
        payload = JSON.parse(eventPayload);
      } catch (e) {
        setEventResult({
          success: false,
          message: 'Invalid JSON payload',
          error: e instanceof Error ? e.message : 'JSON parse error',
        });
        setEventLoading(false);
        return;
      }

      const response = await fetch('/api/whatsapp-webhooks/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setEventResult({
          success: true,
          status: response.status,
          message: 'Webhook event processed successfully',
          data,
        });
      } else {
        setEventResult({
          success: false,
          status: response.status,
          message: 'Webhook event processing failed',
          data,
        });
      }
    } catch (error) {
      setEventResult({
        success: false,
        message: 'Request failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setEventLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">WhatsApp Backend Endpoint Tests</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Test WhatsApp webhook endpoints through the browser
        </p>
      </div>

      {/* Webhook Verification Test */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Webhook Verification Test</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Test GET /api/whatsapp-webhooks/verify endpoint
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="verify-token" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Verify Token
            </label>
            <input
              id="verify-token"
              type="text"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              placeholder="Enter verify token"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="verify-challenge" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Challenge
            </label>
            <input
              id="verify-challenge"
              type="text"
              value={verifyChallenge}
              onChange={(e) => setVerifyChallenge(e.target.value)}
              placeholder="Enter challenge string"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <button
            onClick={testWebhookVerification}
            disabled={verifyLoading}
            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {verifyLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test Webhook Verification
          </button>

          {verifyResult && (
            <div className={`rounded-md p-4 ${verifyResult.success ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
              <div className="flex items-start gap-2">
                {verifyResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                )}
                <div className="flex-1 space-y-2">
                  <p className={`font-semibold ${verifyResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                    {verifyResult.message}
                  </p>
                  {verifyResult.status && (
                    <p className={`text-sm ${verifyResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      Status: {verifyResult.status}
                    </p>
                  )}
                  {verifyResult.data && (
                    <details className="mt-2">
                      <summary className={`cursor-pointer text-sm font-medium ${verifyResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                        Response Data
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-40 text-gray-800 dark:text-gray-200">
                        {typeof verifyResult.data === 'string'
                          ? verifyResult.data
                          : JSON.stringify(verifyResult.data, null, 2)}
                      </pre>
                    </details>
                  )}
                  {verifyResult.error && (
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Error: {verifyResult.error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Webhook Events Test */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Webhook Events Test</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Test POST /api/whatsapp-webhooks/events endpoint
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="event-payload" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Event Payload (JSON)
            </label>
            <textarea
              id="event-payload"
              value={eventPayload}
              onChange={(e) => setEventPayload(e.target.value)}
              placeholder="Enter JSON payload"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-xs"
              rows={15}
            />
          </div>

          <button
            onClick={testWebhookEvents}
            disabled={eventLoading}
            className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {eventLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test Webhook Events
          </button>

          {eventResult && (
            <div className={`rounded-md p-4 ${eventResult.success ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
              <div className="flex items-start gap-2">
                {eventResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                )}
                <div className="flex-1 space-y-2">
                  <p className={`font-semibold ${eventResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                    {eventResult.message}
                  </p>
                  {eventResult.status && (
                    <p className={`text-sm ${eventResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      Status: {eventResult.status}
                    </p>
                  )}
                  {eventResult.data && (
                    <details className="mt-2">
                      <summary className={`cursor-pointer text-sm font-medium ${eventResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                        Response Data
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-40 text-gray-800 dark:text-gray-200">
                        {JSON.stringify(eventResult.data, null, 2)}
                      </pre>
                    </details>
                  )}
                  {eventResult.error && (
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Error: {eventResult.error}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Testing Instructions</h2>
        </div>

        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>
            <strong className="text-gray-900 dark:text-white">Webhook Verification:</strong> Tests the GET endpoint that WhatsApp uses to verify webhook URLs.
            The endpoint should echo back the challenge string if verification succeeds.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Webhook Events:</strong> Tests the POST endpoint that receives webhook events from WhatsApp.
            The endpoint should acknowledge receipt with status 200 and process the event asynchronously.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Note:</strong> This test page uses the frontend's Vite proxy to connect to the backend,
            working around command-line connectivity issues.
          </p>
        </div>
      </div>
    </div>
  );
}
